import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import ChristianAdminSection from '@/components/ChristianAdminSection';
import { Button } from '@/components/ui/button';
import { Loader2, LogOut, Home, Cross } from 'lucide-react';
import { toast } from 'sonner';

const ChristianAdminDashboard = () => {
  const { api, user, logout } = useAuth();
  const navigate = useNavigate();
  const [christianStats, setChristianStats] = useState(null);
  const [christianUsers, setChristianUsers] = useState([]);
  const [christianChildren, setChristianChildren] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    if (!user.is_christian_admin && !user.is_admin) {
      toast.error('Christian admin access required');
      navigate('/christian/admin/login');
      return;
    }
    fetchChristianStats();
  }, [user]);

  const fetchChristianStats = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, childrenRes] = await Promise.all([
        api.get('/admin/christian/stats'),
        api.get('/admin/christian/users?limit=100'),
        api.get('/admin/christian/children?limit=100'),
      ]);
      setChristianStats(statsRes.data);
      setChristianUsers(usersRes.data.users || []);
      setChristianChildren(childrenRes.data.children || []);
    } catch (err) {
      toast.error('Failed to load Christian admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/christian/admin/login');
  };

  if (loading || !christianStats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="w-10 h-10 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur border-b border-purple-800/40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Cross className="w-7 h-7 text-amber-400" />
            <div>
              <h1 className="font-bold text-white text-lg">Christian Admin Console</h1>
              <p className="text-xs text-purple-300">Logged in as {user?.name || user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-purple-200 hover:text-white">
                <Home className="w-4 h-4 mr-1" /> Home
              </Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-purple-200 hover:text-white" data-testid="christian-admin-logout">
              <LogOut className="w-4 h-4 mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <ChristianAdminSection
          christianStats={christianStats}
          christianUsers={christianUsers}
          christianChildren={christianChildren}
          christianLoading={loading}
          fetchChristianStats={fetchChristianStats}
          api={api}
        />
      </main>
    </div>
  );
};

export default ChristianAdminDashboard;
